import React, { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Loader2, ExternalLink, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { THEME_PRESETS, getTheme } from "@/lib/themes";
import { BACKGROUND_PRESETS } from "@/lib/backgrounds";
import { useAuth } from "@/context/AuthContext";

const ALL_LANGS = [
  { code: "it", label: "Italiano" }, { code: "en", label: "English" },
  { code: "fr", label: "Français" }, { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" }, { code: "pt", label: "Português" },
  { code: "hr", label: "Hrvatski" }, { code: "hu", label: "Magyar" },
  { code: "nl", label: "Nederlands" }, { code: "pl", label: "Polski" },
  { code: "ro", label: "Română" }, { code: "ru", label: "Русский" },
  { code: "sk", label: "Slovenčina" },
];

const ANIM_OPTS = [
  { id: "none", name: "Nessuno", desc: "Sfondo statico (default)" },
  { id: "particles", name: "Particelle dorate", desc: "Fine dining, elegante" },
  { id: "bubbles", name: "Bollicine", desc: "Bar, cocktail, pub" },
  { id: "embers", name: "Braci / scintille", desc: "Griglieria, steak house" },
];

const SettingsPage = () => {
  const { checkAuth } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingR, setSavingR] = useState(false);
  const [report, setReport] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [me, setMe] = useState(null);
  const [bannerPdfUploading, setBannerPdfUploading] = useState(false);

  const changePwd = async () => {
    if (!pwd.current || !pwd.next) return toast.error("Compila entrambi i campi");
    if (pwd.next.length < 6) return toast.error("La nuova password deve avere almeno 6 caratteri");
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Password aggiornata");
      setPwd({ current: "", next: "" });
      checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSavingPwd(false); }
  };

  const loadReport = () => api.get("/translations/report").then((r) => setReport(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/restaurant").then((r) => { setRestaurant(r.data); setSettings(r.data.settings); });
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    loadReport();
  }, []);

  const patchSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try { await api.put("/restaurant/settings", patch); toast.success("Salvato"); }
    catch (e) { toast.error("Errore"); }
  };

  const toggleLang = async (code) => {
    if (code === "it") return;
    const langs = settings.languages || ["it"];
    const next = langs.includes(code) ? langs.filter((l) => l !== code) : [...langs, code];
    await patchSettings({ languages: next });
    loadReport();
  };

  const translateAll = async () => {
    const ids = [...new Set((report?.languages || []).flatMap((l) => l.todo_ids))];
    if (!ids.length) return toast.info("Tutto già tradotto");
    setTranslating(true);
    try {
      const r = await api.post("/dishes/translate-bulk", { dish_ids: ids });
      toast.success(`Tradotti ${r.data.translated} piatti`);
      loadReport();
    } catch (e) { toast.error("Errore durante la traduzione"); }
    finally { setTranslating(false); }
  };

  const retranslateAll = async () => {
    setTranslating(true);
    try {
      const r = await api.post("/dishes/translate-bulk", { dish_ids: [], force: true });
      toast.success(`Ritradotti ${r.data.translated} piatti in tutte le lingue`);
      loadReport();
    } catch (e) { toast.error("Errore durante la traduzione"); }
    finally { setTranslating(false); }
  };

  const retranslateLang = async (lang) => {
    setTranslating(true);
    try {
      const r = await api.post("/dishes/translate-bulk", { dish_ids: [], force: true, lang });
      toast.success(`Ritradotti ${r.data.translated} piatti`);
      loadReport();
    } catch (e) { toast.error("Errore durante la traduzione"); }
    finally { setTranslating(false); }
  };

  const saveRestaurant = async () => {
    setSavingR(true);
    try {
      const r = await api.put("/restaurant", { name: restaurant.name });
      setRestaurant(r.data);
      toast.success("Dati locale aggiornati");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSavingR(false); }
  };

  const saveSocial = async () => {
    try {
      const r = await api.put("/restaurant/settings", {
        instagram_url: (settings.instagram_url || "").trim() || null,
        facebook_url: (settings.facebook_url || "").trim() || null,
        website_url: (settings.website_url || "").trim() || null,
        tripadvisor_url: (settings.tripadvisor_url || "").trim() || null,
      });
      setSettings(r.data);
      toast.success("Social aggiornati");
    } catch (e) { toast.error("Errore"); }
  };

  const saveWifi = async () => {
    try {
      const r = await api.put("/restaurant/settings", {
        wifi_name: (settings.wifi_name || "").trim() || null,
        wifi_password: (settings.wifi_password || "").trim() || null,
      });
      setSettings(r.data);
      toast.success("Wi-Fi aggiornato");
    } catch (e) { toast.error("Errore"); }
  };

  const saveReviewEmail = async () => {
    try {
      const r = await api.put("/restaurant/settings", { review_email: (settings.review_email || "").trim() || null });
      setSettings(r.data);
      toast.success("Email recensioni aggiornata");
    } catch (e) { toast.error("Errore"); }
  };

  const saveBannerUrl = () => patchSettings({ banner_link_url: (settings.banner_link_url || "").trim() || null });

  const uploadBannerPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBannerPdfUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await patchSettings({ banner_link_pdf: res.data.path });
    } catch (err) { toast.error("Errore nel caricamento del PDF"); }
    finally { setBannerPdfUploading(false); e.target.value = ""; }
  };

  if (!settings) return <p className="text-muted-foreground">Caricamento…</p>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-cabinet font-bold text-3xl tracking-tight">Impostazioni</h1>
        <p className="text-muted-foreground text-sm mt-1">Personalizza il tuo menù pubblico.</p>
      </div>

      {/* Social */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="social-section">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Social</h2>
        <p className="text-muted-foreground text-sm mb-4">I link opzionali: se lasci vuoto, l'icona non compare nel menù pubblico.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Instagram</label>
            <input value={settings.instagram_url || ""} onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })} data-testid="settings-instagram" placeholder="https://instagram.com/tuolocale" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <label className="text-sm font-medium">Facebook</label>
            <input value={settings.facebook_url || ""} onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })} data-testid="settings-facebook" placeholder="https://facebook.com/tuolocale" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <label className="text-sm font-medium">Sito web</label>
            <input value={settings.website_url || ""} onChange={(e) => setSettings({ ...settings, website_url: e.target.value })} data-testid="settings-website" placeholder="https://www.tuolocale.it" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <label className="text-sm font-medium">TripAdvisor</label>
            <input value={settings.tripadvisor_url || ""} onChange={(e) => setSettings({ ...settings, tripadvisor_url: e.target.value })} data-testid="settings-tripadvisor" placeholder="https://tripadvisor.com/..." className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={saveSocial} data-testid="save-social" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a]">Salva social</button>
        </div>
      </section>

      {/* Wi-Fi */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="wifi-section">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Wi-Fi</h2>
        <p className="text-muted-foreground text-sm mb-4">Mostra rete e password Wi-Fi nella home del menù. I clienti potranno copiarli con un tap. Lascia vuoto per nascondere.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Nome rete (SSID)</label>
            <input value={settings.wifi_name || ""} onChange={(e) => setSettings({ ...settings, wifi_name: e.target.value })} data-testid="settings-wifi-name" placeholder="es. TrattoriaDiamante" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <input value={settings.wifi_password || ""} onChange={(e) => setSettings({ ...settings, wifi_password: e.target.value })} data-testid="settings-wifi-password" placeholder="es. buonappetito2026" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={saveWifi} data-testid="save-wifi" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a]">Salva Wi-Fi</button>
        </div>
      </section>

      {/* Account di accesso (login) */}
      <section className="bg-white rounded-2xl border-2 border-[#002FA7]/25 p-6" data-testid="login-account-section">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Account di accesso</h2>
        <p className="text-muted-foreground text-sm mb-4">L'email con cui accedi al pannello. È <b>diversa</b> dall'"Email per le recensioni" qui sotto.</p>
        <label className="text-sm font-medium">Email di login</label>
        <input value={me?.email || "Nessuna email impostata"} readOnly disabled data-testid="login-email" className="mt-1 w-full rounded-xl border border-input bg-muted/50 text-muted-foreground px-4 py-2.5 cursor-not-allowed focus:outline-none" />
        <p className="text-xs text-muted-foreground mt-1">Per modificare l'email di accesso contatta l'amministratore della piattaforma. La password si cambia qui sotto in "Password account".</p>
      </section>

      {/* Email recensioni */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="review-email-section">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Email per le recensioni</h2>
        <p className="text-muted-foreground text-sm mb-4">Quando un cliente lascia una recensione dal menù pubblico, ricevi il contenuto via email. Lascia vuoto per usare l'email del tuo account ({me?.email || "email di accesso"}).</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="text-sm font-medium">Email destinatario (opzionale)</label>
            <input value={settings.review_email || ""} onChange={(e) => setSettings({ ...settings, review_email: e.target.value })} data-testid="settings-review-email" placeholder="recensioni@tuolocale.it" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <button onClick={saveReviewEmail} data-testid="save-review-email" className="rounded-full bg-[#002FA7] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#00248a] whitespace-nowrap">Salva</button>
        </div>
      </section>

      {/* Restaurant info */}
      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="font-cabinet font-semibold text-lg mb-4">Dati del locale</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Nome</label>
            <input value={restaurant.name} onChange={(e) => setRestaurant({ ...restaurant, name: e.target.value })} data-testid="settings-name" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <label className="text-sm font-medium">Slug (URL) <span className="text-xs text-muted-foreground font-normal">· gestito dall'amministratore</span></label>
            <input value={restaurant.slug} readOnly disabled data-testid="settings-slug" className="mt-1 w-full rounded-xl border border-input bg-muted/50 text-muted-foreground px-4 py-2.5 cursor-not-allowed focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <a href={`/m/${restaurant.slug}`} target="_blank" rel="noopener noreferrer" className="text-sm text-[#002FA7] font-medium flex items-center gap-1.5"><ExternalLink className="w-4 h-4" /> /m/{restaurant.slug}</a>
          <button onClick={saveRestaurant} disabled={savingR} data-testid="save-restaurant-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] flex items-center gap-2">{savingR && <Loader2 className="w-4 h-4 animate-spin" />} Salva</button>
        </div>

        <label className="flex items-center justify-between gap-4 mt-4 rounded-xl border border-border p-4 cursor-pointer" data-testid="toggle-show-name-row">
          <span className="min-w-0">
            <span className="block text-sm font-medium">Mostra il nome del locale in home</span>
            <span className="block text-xs text-muted-foreground mt-0.5">Se il tuo logo contiene già il nome del locale, disattiva questa opzione per evitare che compaia due volte. Il campo "Nome" qui sopra resta comunque usato per l'URL e l'identificazione del locale.</span>
          </span>
          <button type="button" role="switch" aria-checked={settings.show_name_in_home !== false} onClick={() => patchSettings({ show_name_in_home: !(settings.show_name_in_home !== false) })} data-testid="toggle-show-name" className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${settings.show_name_in_home !== false ? "bg-[#002FA7]" : "bg-muted-foreground/40"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${settings.show_name_in_home !== false ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </label>
      </section>

      {/* Password account */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="password-section">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Password account</h2>
        <p className="text-sm text-muted-foreground mb-4">Cambia la password del tuo account. Al primo accesso sostituisci la password temporanea che ti è stata comunicata.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Password attuale</label>
            <input type="password" autoComplete="off" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="pwd-current" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <label className="text-sm font-medium">Nuova password</label>
            <input type="password" autoComplete="new-password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="pwd-next" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={changePwd} disabled={savingPwd} data-testid="change-pwd-btn" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a] flex items-center gap-2 disabled:opacity-60">{savingPwd && <Loader2 className="w-4 h-4 animate-spin" />} Aggiorna password</button>
        </div>
      </section>

      {/* Theme */}
      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Tema grafico</h2>
        <p className="text-sm text-muted-foreground mb-4">Scegli un tema. Logo, sfondo e foto restano sempre modificabili sopra al tema.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          {THEME_PRESETS.map((t) => (
            <button key={t.id} onClick={() => patchSettings({ theme_id: t.id })} data-testid={`theme-${t.id}`} className={`relative rounded-2xl border-2 p-4 text-left transition-colors ${settings.theme_id === t.id ? "border-[#002FA7]" : "border-border hover:border-muted-foreground"}`}>
              {settings.theme_id === t.id && <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#002FA7] text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>}
              <div className="flex gap-1.5 mb-3">
                {t.swatch.map((c) => <span key={c} className="w-6 h-6 rounded-full border border-border" style={{ backgroundColor: c }} />)}
              </div>
              <p className="font-semibold">{t.name}</p>
              <p className="text-xs text-muted-foreground">{t.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Sfondo animato */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="animbg-section">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Sfondo animato della home</h2>
        <p className="text-sm text-muted-foreground mb-4">Aggiunge un'animazione leggera dietro logo, nome e card nella home del menù pubblico. Ottimizzata per il mobile (solo CSS) e si disattiva automaticamente per chi ha impostato "riduci movimento" sul proprio dispositivo.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ANIM_OPTS.map((o) => {
            const on = (settings.animated_bg || "none") === o.id;
            return (
              <button key={o.id} onClick={() => patchSettings({ animated_bg: o.id })} data-testid={`animbg-${o.id}`} className={`relative rounded-2xl border-2 p-4 text-left transition-colors ${on ? "border-[#002FA7]" : "border-border hover:border-muted-foreground"}`}>
                {on && <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#002FA7] text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>}
                <p className="font-semibold text-sm">{o.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{o.desc}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Branding images */}
      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="font-cabinet font-semibold text-lg mb-4">Logo e sfondo</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <ImageUpload value={settings.logo_path} onChange={(p) => patchSettings({ logo_path: p })} label="Logo" testid="settings-logo" aspect="aspect-square" rounded="rounded-2xl" warnTransparency freeAspect allowBgRemoval previewFit="contain" />
          <div>
            <ImageUpload value={settings.background_path} onChange={(p) => patchSettings({ background_path: p })} label="Immagine di sfondo" testid="settings-background" aspect="aspect-[3/4]" presets={BACKGROUND_PRESETS} />
            <p className="text-xs text-muted-foreground mt-2">Consigliato un formato <strong>verticale o quadrato</strong> (es. 3:4 o 1:1): la maggior parte dei clienti apre il menù dallo smartphone. L'immagine viene centrata e adattata automaticamente, senza tagli casuali, sia su mobile che su desktop.</p>
          </div>
        </div>

        {(() => {
          const th = getTheme(settings);
          const bgPath = settings.background_path;
          const isWhite = bgPath === "/backgrounds/white.jpg";
          const t2 = isWhite ? { ...th, bg: "#FFFFFF", text: "#1A1A1A", muted: "#6B6B6B" } : th;
          return (
            <div className="mt-6" data-testid="logo-preview">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Anteprima sul menù</p>
              <div className="relative rounded-2xl overflow-hidden border border-border" style={{ minHeight: 240, backgroundColor: bgPath ? "transparent" : t2.bg }}>
                {bgPath && <img src={fileUrl(bgPath)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                {bgPath && <div className="absolute inset-0" style={{ backgroundColor: `${t2.bg}80` }} />}
                <div className="relative z-10 flex flex-col items-center text-center py-10 px-4">
                  {settings.logo_path ? (
                    <div className="w-full flex items-center justify-center mb-4">
                      <img src={fileUrl(settings.logo_path)} alt="logo" className="w-auto max-w-[70%] max-h-48 object-contain" data-testid="logo-preview-img" />
                    </div>
                  ) : (
                    <p className="text-sm mb-4" style={{ color: t2.muted }}>Carica un logo per vederlo qui</p>
                  )}
                  {settings.show_name_in_home !== false && (
                    <h3 className="text-2xl" style={{ fontFamily: t2.heading, color: t2.accent }}>{restaurant.name}</h3>
                  )}
                  <div className="mt-2 h-px w-14" style={{ background: `linear-gradient(90deg, transparent, ${t2.accent}, transparent)` }} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Così appariranno logo e nome nella home del menù pubblico, sullo sfondo scelto. L'anteprima si aggiorna in tempo reale.</p>
            </div>
          );
        })()}
      </section>

      {/* Banner pubblicitario */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="banner-section">
        <label className="flex items-start justify-between gap-4 cursor-pointer" data-testid="toggle-banner-row">
          <span className="min-w-0">
            <span className="block font-cabinet font-semibold text-lg">Banner pubblicitario</span>
            <span className="block text-muted-foreground text-sm mt-1">Mostra un banner cliccabile in fondo alla home del menù pubblico (es. per pubblicizzare un altro tuo locale o un fornitore). Attivalo e aggiornalo quando vuoi, in autonomia.</span>
          </span>
          <button type="button" role="switch" aria-checked={!!settings.banner_enabled} onClick={() => patchSettings({ banner_enabled: !settings.banner_enabled })} data-testid="toggle-banner" className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors mt-1 ${settings.banner_enabled ? "bg-[#002FA7]" : "bg-muted-foreground/40"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-0.5 ${settings.banner_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </label>

        {settings.banner_enabled && (
          <div className="mt-5 space-y-4" data-testid="banner-config">
            <div>
              <ImageUpload value={settings.banner_image} onChange={(p) => patchSettings({ banner_image: p })} label="Immagine del banner" testid="settings-banner-image" aspect="aspect-[16/6]" />
              <p className="text-xs text-muted-foreground mt-2">Consigliato un formato <strong>orizzontale largo</strong> (es. 16:6). Il banner appare a tutta larghezza in fondo alla home.</p>
            </div>

            <div className="rounded-xl border border-border p-3 space-y-3" data-testid="banner-destination">
              <div>
                <label className="text-sm font-medium">Destinazione al click</label>
                <Select value={settings.banner_link_type || "external"} onValueChange={(v) => patchSettings({ banner_link_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="banner-link-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">Link esterno (URL)</SelectItem>
                    <SelectItem value="pdf">File PDF</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(settings.banner_link_type || "external") === "external" ? (
                <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-muted-foreground">URL del link</label>
                    <input value={settings.banner_link_url || ""} onChange={(e) => setSettings({ ...settings, banner_link_url: e.target.value })} data-testid="banner-link-url-input" placeholder="https://esempio.it" className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7]" />
                  </div>
                  <button onClick={saveBannerUrl} data-testid="banner-link-url-save" className="rounded-full bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00248a] whitespace-nowrap">Salva link</button>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">File PDF</label>
                  {settings.banner_link_pdf ? (
                    <div className="mt-1 flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm" data-testid="banner-link-pdf-current">
                      <FileText className="w-4 h-4 text-[#002FA7]" />
                      <span className="flex-1 truncate text-muted-foreground">PDF caricato</span>
                      <button type="button" onClick={() => patchSettings({ banner_link_pdf: null })} data-testid="banner-link-pdf-remove" className="text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <label className="mt-1 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-3 text-sm cursor-pointer hover:border-[#002FA7]" data-testid="banner-link-pdf-dropzone">
                      {bannerPdfUploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</> : <><FileText className="w-4 h-4" /> Seleziona un PDF</>}
                      <input type="file" accept="application/pdf" className="hidden" onChange={uploadBannerPdf} data-testid="banner-link-pdf-input" disabled={bannerPdfUploading} />
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Languages */}
      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="font-cabinet font-semibold text-lg mb-1">Lingue attive</h2>
        <p className="text-sm text-muted-foreground mb-4">L'italiano è la lingua sorgente. Le altre vengono tradotte con AI dal pannello piatto.</p>
        <div className="flex flex-wrap gap-2">
          {ALL_LANGS.map((l) => {
            const on = (settings.languages || ["it"]).includes(l.code);
            return (
              <button key={l.code} onClick={() => toggleLang(l.code)} disabled={l.code === "it"} data-testid={`lang-toggle-${l.code}`} className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${on ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-muted-foreground border-border hover:border-[#002FA7]"} ${l.code === "it" ? "opacity-70 cursor-default" : ""}`}>
                {l.label}{l.code === "it" && " (sorgente)"}
              </button>
            );
          })}
        </div>
      </section>

      {/* Report traduzioni */}
      <section className="bg-white rounded-2xl border border-border p-6" data-testid="translation-report">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="font-cabinet font-semibold text-lg">Stato traduzioni</h2>
            <p className="text-sm text-muted-foreground mt-1">Riepilogo delle voci tradotte per ogni lingua attiva{report ? ` · ${report.total_dishes} piatti totali` : ""}.</p>
          </div>
          {report && report.languages.length > 0 && (
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <button onClick={translateAll} disabled={translating} data-testid="translate-all-btn" className="rounded-full bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00248a] flex items-center gap-2 disabled:opacity-60">{translating && <Loader2 className="w-4 h-4 animate-spin" />} Traduci le modifiche</button>
              <button onClick={retranslateAll} disabled={translating} data-testid="retranslate-all-btn" className="text-xs font-medium text-[#002FA7] hover:underline disabled:opacity-60">Ritraduci tutto da zero</button>
            </div>
          )}
        </div>
        {!report ? (
          <p className="text-sm text-muted-foreground">Caricamento…</p>
        ) : report.languages.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nessuna lingua aggiuntiva attiva. Attiva altre lingue qui sopra per abilitare le traduzioni.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {report.languages.map((l) => {
              const pending = l.missing + l.stale;
              const done = pending === 0;
              return (
                <div key={l.lang} data-testid={`report-lang-${l.lang}`} className="rounded-xl border border-border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold">{(ALL_LANGS.find((x) => x.code === l.lang) || {}).label || l.name}</p>
                    {done
                      ? <span className="text-xs font-medium text-emerald-600 flex items-center gap-1"><Check className="w-4 h-4" /> Completo</span>
                      : <span className="text-xs font-medium text-amber-600">{pending} da tradurre</span>}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                      <span className="text-muted-foreground">{l.complete} completi</span>
                      {l.missing > 0 && <span className="text-amber-600">{l.missing} mancanti</span>}
                      {l.stale > 0 && <span className="text-orange-600">{l.stale} da aggiornare</span>}
                    </div>
                    <button onClick={() => retranslateLang(l.lang)} disabled={translating} data-testid={`retranslate-lang-${l.lang}`} className="text-xs font-medium text-[#002FA7] hover:underline disabled:opacity-60 flex-shrink-0">Ritraduci</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <section className="bg-white rounded-2xl border border-border p-6">
        <h2 className="font-cabinet font-semibold text-lg">Visibilità di menù e categorie</h2>
        <p className="text-sm text-muted-foreground mt-1">Per mostrare o nascondere un intero menù (es. "Menù del giorno" o "Drink List") o una singola categoria dal menù pubblico, usa l'icona a forma di occhio nella sezione <strong>Menù</strong>. I contenuti nascosti restano salvati e li riattivi con un tap.</p>
      </section>
    </div>
  );
};

export default SettingsPage;
